// ── APP SHELL: routing, mobile drawer, construction notice ───────────────────
const { Nav, Footer, LeafMark, Ico: I4 } = window.BCKit;
const { Home } = window.BCHome;
const { RVSites, TentSites, Cabins } = window.BCStays;
const { Activities, Events, Rules, Gallery, Blog } = window.BCContent;

const PAGES = {
  home: Home, rv: RVSites, tent: TentSites, cabins: Cabins,
  activities: Activities, events: Events, gallery: Gallery, blog: Blog, rules: Rules,
};

// Real paths (home → "/", rv → "/rv", …); maps live in bc-store.jsx, server mirrors them.
const idToPath = window.BCStore.idToPath;
const pathToId = window.BCStore.pathToId;

// Intercept a nav click so it routes in-app, but leave modified / middle clicks to
// the browser (so ⌘/ctrl-click still opens the real href in a new tab).
function navClick(id, go) {
  return (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(id);
  };
}

// Apply the resolved SEO for a route to <head>, updating the server-rendered
// [data-seo] tags in place (creating any that are missing) so there's exactly one
// of each — no duplicates between server render and client soft-navigation.
function applySeo(id) {
  try {
    const data = window.BCStore.getData ? window.BCStore.getData() : window.BCStore.DEFAULTS;
    const s = window.BCStore.resolveSeo(id, data);
    const head = document.head;
    const find = (k) => head.querySelector('[data-seo="' + k + '"]');
    const meta = (k, attr, attrVal, content) => {
      let el = find(k);
      if (!content) { if (el) el.parentNode.removeChild(el); return; }
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, attrVal); el.setAttribute("data-seo", k); head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const link = (k, rel, href) => {
      let el = find(k);
      if (!href) { if (el) el.parentNode.removeChild(el); return; }
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); el.setAttribute("data-seo", k); head.appendChild(el); }
      el.setAttribute("href", href);
    };

    document.title = s.title;                      // updates the <title data-seo="title"> text
    meta("description", "name", "description", s.description);
    link("canonical", "canonical", s.canonical);
    meta("robots", "name", "robots", s.robots);
    meta("og:title", "property", "og:title", s.ogTitle);
    meta("og:description", "property", "og:description", s.ogDescription);
    meta("og:type", "property", "og:type", s.ogType);
    meta("og:url", "property", "og:url", s.ogUrl);
    meta("og:site_name", "property", "og:site_name", s.siteName);
    meta("og:image", "property", "og:image", s.ogImage);
    meta("twitter:card", "name", "twitter:card", s.twitterCard);
    meta("twitter:title", "name", "twitter:title", s.twitterTitle);
    meta("twitter:description", "name", "twitter:description", s.twitterDescription);
    meta("twitter:image", "name", "twitter:image", s.twitterImage);
    meta("twitter:site", "name", "twitter:site", s.twitterSite);
    link("favicon", "icon", s.favicon);

    let ld = find("ld");
    if (s.jsonLd) {
      if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.setAttribute("data-seo", "ld"); head.appendChild(ld); }
      ld.textContent = JSON.stringify(s.jsonLd);
    } else if (ld) { ld.parentNode.removeChild(ld); }
  } catch (_) { /* never let SEO break the page */ }
}

const DRAWER_LINKS = [
  ["home","Home"],["rv","RV Sites"],["tent","Tent Sites"],["cabins","Cabins"],
  ["activities","Activities"],["events","Events"],["gallery","Gallery"],["blog","Blog"],
  ["rules","Rules & Regulations"],
];

// Notice board — every enabled notice renders as its own colored bar, stacked
// directly beneath the nav. On the home page the nav is fixed (overlaying the
// hero), so the board is fixed just below it; on inner pages the nav is sticky
// in flow, so the board is sticky at the nav's height.
const NoticeBoard = ({ onHero, top }) => {
  const [data] = window.BCStore.useStore();
  const ann = data.announcement || {};
  const [dismissed, setDismissed] = React.useState(() => new Set());
  if (!ann.on) return null;
  const list = (ann.notices || []).filter(n => n.on && !dismissed.has(n.id));
  if (!list.length) return null;
  return (
    <div className="noticeboard" style={{ position: onHero ? "fixed" : "sticky", top, left: 0, right: 0 }}>
      {list.map(n => {
        const Ic = (window.BCKit.Ico[n.icon]) || window.BCKit.Ico.info;
        return (
          <div key={n.id} className={`notice notice--${n.theme || "amber"}`}>
            <span className="notice__ico"><Ic style={{ width: 18, height: 18 }} /></span>
            <div className="notice__txt">{n.text}{n.link && <> <a href={n.link}>Learn more</a></>}</div>
            <button onClick={() => setDismissed(s => { const m = new Set(s); m.add(n.id); return m; })} className="notice__x" aria-label="Dismiss notice">✕</button>
          </div>
        );
      })}
    </div>
  );
};

// Error boundary — if any component throws (e.g. a malformed content.json from
// the admin), the whole page would otherwise unmount to a blank screen. Instead,
// show an on-brand recovery card that keeps the booking path (phone) alive.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { try { console.error("Butterfly Camp render error:", error, info); } catch (e) {} }
  render() {
    if (!this.state.error) return this.props.children;
    let c = {}, brandLogo = '';
    try {
      const d = window.BCStore.getData ? window.BCStore.getData() : window.BCStore.DEFAULTS;
      c = (d && d.contact) || {};
      brandLogo = (d && d.brand && d.brand.logo) || '';
    } catch (e) {
      c = ((window.BCStore && window.BCStore.DEFAULTS && window.BCStore.DEFAULTS.contact)) || {};
    }
    return (
      <div role="alert" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px", background: "var(--paper)" }}>
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 22 }}>
            <div style={{ width: 48, height: 48, borderRadius: 13, background: brandLogo ? "transparent" : "var(--green)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {brandLogo ? <img src={brandLogo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : <LeafMark size={26} color="#fff" />}
            </div>
            <span className="serif" style={{ fontSize: 21, fontWeight: 700, color: "var(--ink)" }}>Butterfly Camping Resort</span>
          </div>
          <h1 className="display" style={{ fontSize: 30, marginBottom: 12 }}>This page didn’t load right</h1>
          <p style={{ fontSize: 16, color: "var(--muted)", lineHeight: 1.6, marginBottom: 26 }}>
            Sorry about that. You can still reach us directly. We answer the phone ourselves and will get you booked.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
            {c.phone && (
              <a href={`tel:${window.BCStore.telHref(c.phone)}`} className="btn btn--amber btn--lg" style={{ textDecoration: "none" }}>
                <I4.phone style={{ width: 17, height: 17 }} />Call {c.phone}
              </a>
            )}
            <button type="button" className="btn btn--outline" onClick={() => window.location.reload()}>Reload the page</button>
          </div>
          {(c.addr1 || c.email) && (
            <div style={{ marginTop: 24, fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7 }}>
              {c.email && <div><a href={`mailto:${c.email}`} style={{ color: "var(--green)", fontWeight: 600 }}>{c.email}</a></div>}
              {c.addr1 && <div>{c.addr1}{c.addr2 ? `, ${c.addr2}` : ""}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }
}

// Mobile call bar — a fixed thumb-zone "call to book" CTA for phones/tablets.
// Appears once the user scrolls roughly past the hero so it never covers the
// hero's own CTAs, and stays hidden while the drawer (with its own call action)
// is open. CSS hides it entirely above 880px. See `.callbar` in index.html.
const MobileCallBar = ({ drawerOpen }) => {
  const [data] = window.BCStore.useStore();
  const phone = data.contact.phone;
  const season = data.season || {};
  const [past, setPast] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setPast(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);
  if (!phone) return null;
  const visible = past && !drawerOpen;
  return (
    <div className={`callbar ${visible ? "callbar--in" : ""}`} aria-hidden={visible ? undefined : true}>
      <div className="callbar__txt">
        <div className="callbar__t">Reserve your site</div>
        <div className="callbar__s">{season.chip ? `Open ${season.chip}` : "We match you to a site by phone"}</div>
      </div>
      <a className="btn btn--amber" href={`tel:${window.BCStore.telHref(phone)}`} tabIndex={visible ? undefined : -1}>
        <I4.phone style={{ width: 16, height: 16 }} />Call to book
      </a>
    </div>
  );
};

function App() {
  const [data] = window.BCStore.useStore();
  const contact = data.contact;
  const brand = data.brand || {};
  const [route, setRoute] = React.useState(() => {
    // One-time back-compat: rewrite an old hash link (/#rv) to a real path (/rv).
    const hashId = (window.location.hash || "").replace("#", "");
    if (PAGES[hashId]) {
      try { window.history.replaceState({ id: hashId }, "", idToPath(hashId)); } catch (_) {}
      return hashId;
    }
    return pathToId(window.location.pathname) || "home";
  });
  const [drawer, setDrawer] = React.useState(false);
  const navRef = React.useRef(null);
  const [navH, setNavH] = React.useState(74);

  const go = React.useCallback((p) => {
    // Ids are page keys, plus "blog/<slug>" for individual post pages.
    const id = PAGES[p] ? p : (typeof p === "string" && p.indexOf("blog/") === 0 ? p : "home");
    setRoute(id);
    setDrawer(false);
    const url = idToPath(id);
    try { if (window.location.pathname !== url) window.history.pushState({ id }, "", url); } catch (_) {}
    window.scrollTo({ top: 0, behavior: "instant" in document.documentElement.style ? "instant" : "auto" });
  }, []);

  // Back/forward through real history entries.
  React.useEffect(() => {
    const onPop = () => {
      const id = pathToId(window.location.pathname);
      if (!id) { window.location.replace(window.location.pathname); return; }
      setRoute(id);
      setDrawer(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Keep <head> SEO tags in sync with the current route + live content (SSE saves).
  // data.blog matters too: post pages derive their tags from the post itself.
  React.useEffect(() => { applySeo(route); }, [route, data.seo, data.brand, data.blog]);

  React.useEffect(() => {
    document.body.style.overflow = drawer ? "hidden" : "";
  }, [drawer]);

  // Measure the nav so the notice board can tuck just beneath it.
  React.useLayoutEffect(() => {
    const measure = () => { if (navRef.current) setNavH(navRef.current.offsetHeight); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [route]);

  // "blog/<slug>" routes render the Blog page with the slug as `sub`; the nav
  // and drawer highlight by the base page so Journal stays lit on post pages.
  const base = route.split("/")[0];
  const sub = route.indexOf("/") > 0 ? route.slice(route.indexOf("/") + 1) : null;
  const Page = PAGES[base] || Home;
  const onHero = base === "home";

  return (
    <React.Fragment>
      <Nav navRef={navRef} route={base} go={go} onHero={onHero} openDrawer={() => setDrawer(true)} />
      <NoticeBoard onHero={onHero} top={navH} />
      <Page go={go} sub={sub} />
      <Footer go={go} />
      <MobileCallBar drawerOpen={drawer} />

      {/* Mobile drawer */}
      <div className={`drawer-scrim ${drawer ? "drawer-scrim--open" : ""}`} style={{ pointerEvents: drawer ? "auto" : "none" }} onClick={() => setDrawer(false)} />
      <aside className={`drawer ${drawer ? "drawer--open" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 64, height: 64, flexShrink: 0, background: brand.logo ? "transparent" : "rgba(255,255,255,.14)", borderRadius: brand.logo ? 0 : 16, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {brand.logo ? <img src={brand.logo} alt={brand.name || "logo"} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} /> : <LeafMark size={34} color="#fff" />}
            </div>
            <span className="serif" style={{ fontSize: 19, fontWeight: 700, color: "#fff" }}>Butterfly Camp</span>
          </div>
          <span onClick={() => setDrawer(false)} style={{ color: "rgba(255,255,255,.7)", fontSize: 26, cursor: "pointer", lineHeight: 1 }}>✕</span>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {DRAWER_LINKS.filter(([id]) => id === "home" || data.nav[id] !== false).map(([id, t]) => (
            <a key={id} href={idToPath(id)} className="drawer__link" style={base === id ? { color: "var(--amber)" } : {}} onClick={navClick(id, go)}>{t}</a>
          ))}
        </div>
        <a href={`tel:${window.BCStore.telHref(contact.phone)}`} style={{ marginTop: 22, background: "#fff", borderRadius: 14, padding: "16px 18px", textDecoration: "none", display: "block" }}>
          <div className="eyebrow" style={{ color: "var(--muted)" }}>Tap to call</div>
          <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: "var(--green)", marginTop: 4 }}>{contact.phone}</div>
          {data.officeHours[0] && (
            <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 4 }}>
              {data.officeHours[0][0]} {data.officeHours[0][1]}{data.officeHours[1] ? ` · ${data.officeHours[1][0]} ${data.officeHours[1][1]}` : ""}
            </div>
          )}
        </a>
      </aside>
    </React.Fragment>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary><App /></ErrorBoundary>
);
